/**
 * BottomSheetSelect (Inventory Screens Step 5.3 Phase 1 — D-5.3-8) — the
 * field zone's oversized enum picker. PRESENTATION ONLY: it renders whatever
 * options[] it is handed (refdata is SSOT — nothing hardcoded) and speaks the
 * exact value semantics of the <select> it replaces: '' means unset, a row
 * tap hands back `null` for the "— none —" row or the option string
 * otherwise; the PARENT owns commit-if-changed and the close navigation.
 *
 * Why it exists: addendum §F "oversized daylight pulldowns" — a native
 * <select> popup is small and low-contrast outdoors; this sheet gives
 * full-width, 60px-row, one-tap targets.
 *
 * Close coverage (all paths resolve through the SAME onClose → the host's
 * navigate(-1) sentinel pop, D-5.3-8):
 *   - row tap        → onSelect(next) (host commits then closes)
 *   - backdrop tap   → onClose
 *   - drag-down      → touch drag on the grab zone; > 80px release closes
 *   - Escape         → capture-phase window listener + stopPropagation so
 *                      ModalShell's bubble-phase Escape (ModalShell.jsx:102-109)
 *                      never fires — the sheet closes, the modal stays.
 *   - hardware back  → pops the history entry; the host derives isOpen from
 *                      location.state so the sheet unmounts on its own.
 *
 * Escape-hatch row: a current value not present in options[] still renders
 * (selected) — mirrors the <select>'s extra-option arm exactly.
 *
 * offListCurrent: 'option' (DEFAULT — every caller before OEM, unchanged) |
 * 'readonly' (OEM arc Phase 2 — the fixed-list rules FixedListSelect applies
 * on desktop, in this component's markup):
 *   - an off-list current value is NEVER a row: it shows as read-only text in
 *     the header (.bss-grab), outside the scrolling listbox, so it cannot be
 *     scrolled past, tapped, or carry the ✓;
 *   - the "— none —" row is not rendered: blank is not a choice (TBD and
 *     OTHER carry those meanings);
 *   - off-list = case-sensitive membership in options[] ('Toyota' is off-list
 *     against 'TOYOTA' and the rep picks TOYOTA);
 *   - an empty options[] shows a one-line note instead of a blank sheet.
 * Stacking: rendered inside the ModalShell dialog subtree (no portal —
 * ModalShell has no transform, so position:fixed children bind to the
 * viewport) at z-index --z-modal; being later in the dialog's stacking
 * context paints it above the modal chrome.
 */

import React, { useEffect, useRef, useState } from 'react';
import './BottomSheetSelect.css';

const DRAG_CLOSE_PX = 80;

export default function BottomSheetSelect({
  isOpen,
  label,
  value,
  options = [],
  onSelect,
  onClose,
  offListCurrent = 'option',
}) {
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef(null);

  // Escape (desktop) — capture + stopPropagation: close the SHEET only.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen, onClose]);

  // Never carry a half-finished drag across open/close cycles.
  useEffect(() => {
    setDragY(0);
    dragStartY.current = null;
  }, [isOpen]);

  if (!isOpen) return null;

  const current = value ?? '';
  const showCurrentExtra = current !== '' && !options.includes(current);
  const fixedList = offListCurrent === 'readonly';

  const handleDragStart = (e) => {
    dragStartY.current = e.touches[0].clientY;
  };
  const handleDragMove = (e) => {
    if (dragStartY.current == null) return;
    setDragY(Math.max(0, e.touches[0].clientY - dragStartY.current));
  };
  const handleDragEnd = () => {
    if (dragStartY.current == null) return;
    const shouldClose = dragY > DRAG_CLOSE_PX;
    dragStartY.current = null;
    setDragY(0);
    if (shouldClose) onClose();
  };

  const renderRow = (optValue, display, extraClass = '') => {
    const selected = current === (optValue ?? '');
    return (
      <button
        key={optValue === null ? '__none__' : optValue}
        type="button"
        className={`bss-option${extraClass}${selected ? ' bss-option-selected' : ''}`}
        role="option"
        aria-selected={selected}
        data-value={optValue ?? ''}
        onClick={() => onSelect(optValue)}
      >
        <span className="bss-option-label">{display}</span>
        {selected && <span className="bss-option-check" aria-hidden="true">✓</span>}
      </button>
    );
  };

  return (
    <div data-testid="field-bottom-sheet">
      <div className="bss-backdrop" data-testid="bss-backdrop" onClick={onClose} />
      <div
        className="bss-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={label}
        style={dragY > 0 ? { transform: `translateY(${dragY}px)`, animation: 'none' } : undefined}
      >
        <div
          className="bss-grab"
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
          onTouchCancel={handleDragEnd}
        >
          <div className="bss-handle" aria-hidden="true" />
          <div className="bss-title">{label}</div>
          {fixedList && showCurrentExtra && (
            <div className="bss-current" data-testid="bss-current">
              <span className="bss-current-label">Current:</span>{' '}
              <span className="bss-current-value">{current}</span>
              <span className="bss-current-note"> — not on the list</span>
            </div>
          )}
        </div>
        <div className="bss-options" role="listbox" aria-label={`${label} options`}>
          {/* '' ⇄ null unset semantics — identical to the <select>'s blank option */}
          {!fixedList && renderRow(null, '— none —', ' bss-option-none')}
          {options.map((opt) => renderRow(opt, opt))}
          {!fixedList && showCurrentExtra && renderRow(current, current)}
          {fixedList && options.length === 0 && (
            <div className="bss-empty" data-testid="bss-empty">
              No list on this device yet — it loads the next time you are online.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
